import React, { useEffect, useState } from "react";
import { Alert, Card, Spinner } from "flowbite-react";
import { useQuery } from "react-query";
import {
  HiOutlineBookOpen,
  HiOutlineCheckCircle,
  HiOutlineCreditCard,
  HiOutlineExclamation,
  HiOutlineGift,
  HiOutlineInformationCircle,
  HiOutlineMail,
  HiOutlinePencilAlt,
  HiOutlineReceiptRefund,
  HiOutlineShieldCheck,
  HiOutlineSwitchVertical,
} from "react-icons/hi";
import type { IconType } from "react-icons";

import SubTitle from "../../components/SubTitle";
import Button from "../../components/Button";
import { getMemberCredits } from "../../api/memberCredits/actions";
import { memberCreditsQueryKey } from "../../api/memberCredits/queryKeys";
import {
  AboCreditAdjustmentResponse,
  AboCreditHistoryEntry,
  CREDITS_ADJUST_DEFAULT_LIMIT,
  CREDITS_HISTORY_PAGE_SIZE,
  CREDITS_HISTORY_TYPES,
  CreditsHistoryType,
} from "../../api/memberCredits/types";
import { usePermissions } from "../../hooks/usePermissions";
import { formatCount } from "../../utils/formatCount";
import { formatDateTime } from "../../../utils/date";
import MemberCreditsAdjustDrawer from "./MemberCreditsAdjustDrawer";
import {
  adjustmentResultMessage,
  formatSignedCredits,
} from "./memberCreditsFormat";
import {
  memberCreditsStrings as S,
  formatMessage,
} from "./MemberCreditsTab.strings";

interface MemberCreditsTabProps {
  customerId: string;
  /** Shown in the adjustment confirmation ("Add 5 credits to Jane Doe?"). */
  memberName: string;
}

const REASON_ICONS: Record<string, IconType> = {
  purchase: HiOutlineCreditCard,
  subscription_credit_grant: HiOutlineGift,
  book_print_debit: HiOutlineBookOpen,
  mod_order_debit: HiOutlineMail,
  refund: HiOutlineReceiptRefund,
  adjustment: HiOutlinePencilAlt,
  downgrade_clawback: HiOutlineSwitchVertical,
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return S.none;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? S.none : formatDateTime(date);
};

const reasonLabel = (reason: string): string =>
  S.history.reasons[reason] ?? reason;

const refTypeLabel = (refType: string): string =>
  S.history.refTypes[refType] ?? refType;

const changeTone = (delta: number): string =>
  delta > 0
    ? "bg-emerald-50 text-emerald-800"
    : delta < 0
    ? "bg-red-50 text-red-700"
    : "bg-neutral-100 text-neutral-700";

const HistoryRow: React.FC<{ entry: AboCreditHistoryEntry }> = ({ entry }) => {
  const clamped =
    typeof entry.requestedDelta === "number" &&
    entry.requestedDelta !== entry.delta;
  const Icon = REASON_ICONS[entry.reason] ?? HiOutlineInformationCircle;
  return (
    <tr className="border-b border-neutral-200 align-top" data-testid="credits-history-row">
      <td className="px-3 py-3 whitespace-nowrap text-neutral-800">
        {formatDate(entry.createdAt)}
      </td>
      <td className="px-3 py-3">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-neutral-100">
            <Icon className="h-4 w-4 text-neutral-600" aria-hidden="true" />
          </span>
          <span className="font-medium text-neutral-900">
            {reasonLabel(entry.reason)}
          </span>
        </span>
      </td>
      <td className="px-3 py-3 text-right" data-testid="credits-history-change">
        <span
          className={`inline-flex min-w-[44px] justify-center rounded-full px-2 py-0.5 font-semibold tabular-nums ${changeTone(
            entry.delta
          )}`}
        >
          {formatSignedCredits(entry.delta)}
        </span>
        {clamped && (
          <p
            className="mt-1 text-xs text-neutral-500"
            data-testid="credits-history-requested"
          >
            {formatMessage(S.history.requested, {
              amount: formatSignedCredits(entry.requestedDelta as number),
            })}
          </p>
        )}
      </td>
      <td className="px-3 py-3 text-right font-medium tabular-nums">
        {formatCount(entry.balanceAfter)}
      </td>
      <td className="px-3 py-3 text-sm">
        <p className="text-neutral-600">
          <span>{refTypeLabel(entry.refType)}</span>
          {entry.refId && (
            <span className="ml-1 break-all font-mono text-xs text-neutral-500">
              {entry.refId}
            </span>
          )}
        </p>
        {entry.actor && (
          <p className="mt-0.5 break-all text-neutral-800">
            {formatMessage(S.history.byOperator, { operator: entry.actor })}
          </p>
        )}
        {entry.note && (
          <p className="mt-0.5 whitespace-pre-wrap break-words text-neutral-600">
            “{entry.note}”
          </p>
        )}
      </td>
    </tr>
  );
};

const LedgerPill: React.FC<{ live: boolean }> = ({ live }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
      live ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
    }`}
    data-testid="credits-ledger-pill"
  >
    {live ? (
      <HiOutlineShieldCheck className="h-4 w-4" aria-hidden="true" />
    ) : (
      <HiOutlineInformationCircle className="h-4 w-4" aria-hidden="true" />
    )}
    {live ? S.balance.ledgerLive : S.balance.ledgerNotLive}
  </span>
);

const FilterChips: React.FC<{
  value: CreditsHistoryType;
  disabled: boolean;
  onChange: (value: CreditsHistoryType) => void;
}> = ({ value, disabled, onChange }) => (
  <div
    role="group"
    aria-label={S.history.filters.label}
    className="flex flex-wrap gap-2"
    data-testid="credits-history-filters"
  >
    {CREDITS_HISTORY_TYPES.map((type) => {
      const active = type === value;
      return (
        <button
          key={type}
          type="button"
          aria-pressed={active}
          disabled={disabled}
          onClick={() => onChange(type)}
          className={`min-h-[36px] rounded-full border px-3 text-sm font-semibold transition-colors ${
            active
              ? "border-neutral-900 bg-neutral-900 text-white"
              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
          }`}
        >
          {S.history.filters[type]}
        </button>
      );
    })}
  </div>
);

/**
 * Credits tab of Customer Information (abo-member-credits, redesign option
 * 1): a balance strip (print balance, last movement, ledger status and, for
 * operators with `credits_adjust` once the ledger is authoritative, the
 * "Adjust credits" button that opens the side drawer), then the ledger
 * history with type filters and pagination. Loads
 * `GET /v1/abo/customers/:id/credits`.
 */
const MemberCreditsTab: React.FC<MemberCreditsTabProps> = ({
  customerId,
  memberName,
}) => {
  const [page, setPage] = useState(0);
  const [type, setType] = useState<CreditsHistoryType>("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [result, setResult] = useState<AboCreditAdjustmentResponse | null>(
    null
  );
  const [lastMovement, setLastMovement] =
    useState<AboCreditHistoryEntry | null>(null);
  const { canAdjustCredits } = usePermissions();

  useEffect(() => {
    setPage(0);
    setType("all");
    setDrawerOpen(false);
    setResult(null);
    setLastMovement(null);
  }, [customerId]);

  const query = useQuery(
    [...memberCreditsQueryKey(customerId), page, type],
    () => getMemberCredits(customerId, page, CREDITS_HISTORY_PAGE_SIZE, type),
    {
      enabled: Boolean(customerId),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      // A failure shows the load-error notice at once; the operator retries.
      retry: false,
    }
  );

  const data = query.data;
  // Read defensively: a payload without `history` (an unexpected answer)
  // renders as an empty history instead of breaking the customer page.
  const entries = Array.isArray(data?.history?.data) ? data.history.data : [];
  const total = Number(data?.history?.meta?.total) || entries.length;
  const perPage = Number(data?.history?.meta?.perPage) || CREDITS_HISTORY_PAGE_SIZE;
  const balance = typeof data?.balance === "number" ? data.balance : null;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const canAdjust =
    data?.ledgerAuthoritative === true && balance !== null && canAdjustCredits();

  // The newest movement of the member, known from the unfiltered first page.
  useEffect(() => {
    if (page !== 0 || type !== "all" || !data || query.isPreviousData) {
      return;
    }
    const newest = Array.isArray(data.history?.data) ? data.history.data[0] : null;
    setLastMovement(newest ?? null);
  }, [data, page, type, query.isPreviousData]);

  const changeFilter = (value: CreditsHistoryType) => {
    setType(value);
    setPage(0);
  };

  const handleApplied = (applied: AboCreditAdjustmentResponse) => {
    setResult(applied);
    setDrawerOpen(false);
  };

  const from = total === 0 ? 0 : page * perPage + 1;
  const to = Math.min(total, page * perPage + entries.length);

  return (
    <div className="flex flex-col gap-4" data-testid="member-credits-tab">
      {query.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}

      {query.isError && (
        <Alert
          color="failure"
          icon={HiOutlineExclamation}
          data-testid="credits-load-error"
        >
          <div className="flex flex-wrap items-center gap-4">
            <span>{S.loadError}</span>
            <Button
              variant="LIGHT"
              size="xs"
              isProcessing={query.isFetching}
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              {S.retry}
            </Button>
          </div>
        </Alert>
      )}

      {data && (
        <>
          {data.ledgerAuthoritative === false && (
            <Alert
              color="info"
              icon={HiOutlineInformationCircle}
              data-testid="credits-notice-not-authoritative"
            >
              {S.notice.notAuthoritative}
            </Alert>
          )}

          <Card data-testid="credits-balance">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div className="flex flex-wrap items-center gap-6 md:gap-8">
                <div>
                  <p className="text-sm text-neutral-500">
                    {S.balance.printCredits}
                  </p>
                  <p
                    className="text-4xl font-bold leading-tight text-neutral-900 tabular-nums"
                    data-testid="credits-balance-value"
                  >
                    {balance === null ? S.none : formatCount(balance)}
                  </p>
                </div>
                {lastMovement && (
                  <div
                    className="border-l border-neutral-200 pl-6 md:pl-8"
                    data-testid="credits-last-movement"
                  >
                    <p className="text-sm text-neutral-500">
                      {S.balance.lastMovement}
                    </p>
                    <p className="font-semibold text-neutral-900">
                      {formatMessage(S.balance.lastMovementValue, {
                        change: formatSignedCredits(lastMovement.delta),
                        date: formatDate(lastMovement.createdAt),
                      })}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {reasonLabel(lastMovement.reason)}
                    </p>
                  </div>
                )}
                <LedgerPill live={data.ledgerAuthoritative === true} />
              </div>
              {canAdjust && (
                <Button
                  variant="DARK"
                  data-testid="credits-adjust-open"
                  onClick={() => setDrawerOpen(true)}
                >
                  <span className="inline-flex items-center gap-2">
                    <HiOutlinePencilAlt className="h-4 w-4" aria-hidden="true" />
                    {S.balance.adjustButton}
                  </span>
                </Button>
              )}
            </div>
          </Card>

          {result && (
            <Alert
              color="success"
              icon={HiOutlineCheckCircle}
              onDismiss={() => setResult(null)}
              data-testid="credits-adjust-result"
            >
              {adjustmentResultMessage(result)}
            </Alert>
          )}

          <Card data-testid="credits-history">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SubTitle>{S.history.heading}</SubTitle>
              <FilterChips
                value={type}
                disabled={query.isFetching}
                onChange={changeFilter}
              />
            </div>
            {entries.length === 0 ? (
              <p className="text-sm text-neutral-500" data-testid="credits-empty">
                {type === "all" ? S.history.empty : S.history.emptyFiltered}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table
                    className={`w-full min-w-[720px] text-sm text-left text-neutral-700 ${
                      query.isPreviousData && query.isFetching ? "opacity-60" : ""
                    }`}
                    aria-busy={query.isPreviousData && query.isFetching}
                  >
                    <thead className="text-xs uppercase tracking-wide text-neutral-500">
                      <tr className="border-b border-neutral-200">
                        <th scope="col" className="px-3 py-2 font-semibold">
                          {S.history.columns.date}
                        </th>
                        <th scope="col" className="px-3 py-2 font-semibold">
                          {S.history.columns.movement}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-semibold">
                          {S.history.columns.change}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-semibold">
                          {S.history.columns.balanceAfter}
                        </th>
                        <th scope="col" className="px-3 py-2 font-semibold">
                          {S.history.columns.details}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <HistoryRow key={entry.id} entry={entry} />
                      ))}
                    </tbody>
                  </table>
                </div>
                <nav
                  className="flex flex-wrap items-center justify-between gap-3"
                  aria-label={S.history.pagination.label}
                  data-testid="credits-pagination"
                >
                  <span className="text-sm text-neutral-500">
                    {formatMessage(S.history.pagination.showing, {
                      from: formatCount(from),
                      to: formatCount(to),
                      total: formatCount(total),
                    })}
                  </span>
                  {pages > 1 && (
                    <div className="flex gap-2">
                      <Button
                        variant="LIGHT"
                        size="xs"
                        disabled={page === 0 || query.isFetching}
                        onClick={() => setPage((current) => Math.max(0, current - 1))}
                      >
                        {S.history.pagination.previous}
                      </Button>
                      <Button
                        variant="LIGHT"
                        size="xs"
                        disabled={page + 1 >= pages || query.isFetching}
                        onClick={() => setPage((current) => current + 1)}
                      >
                        {S.history.pagination.next}
                      </Button>
                    </div>
                  )}
                </nav>
              </>
            )}
          </Card>

          {canAdjust && (
            <MemberCreditsAdjustDrawer
              key={customerId}
              open={drawerOpen}
              customerId={customerId}
              memberName={memberName}
              balance={balance ?? 0}
              adjustmentLimit={data.adjustmentLimit || CREDITS_ADJUST_DEFAULT_LIMIT}
              onClose={() => setDrawerOpen(false)}
              onApplied={handleApplied}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MemberCreditsTab;
